'use client';

import { Content } from '@prismicio/client';
import { PrismicNextImage } from '@prismicio/next';
import {
  JSXMapSerializer,
  PrismicRichText,
  SliceComponentProps,
} from '@prismicio/react';
import { FC } from 'react';
import { Bounded } from '@/components/common/Bounded';
import { Heading } from '@/components/common/Heading';

const components: JSXMapSerializer = {
  heading1: ({ children }) => (
    <Heading as="h1" size="xl" className="text-center mb-12">
      {children}
    </Heading>
  ),
};

/**
 * Props for `AvatarGrid`.
 */
export type AvatarGridProps = SliceComponentProps<Content.AvatarGridSlice>;

/**
 * Component for "AvatarGrid" Slices.
 */
const AvatarGrid: FC<AvatarGridProps> = ({ slice }) => {
  const { variation } = slice;

  type ProfileItem = Content.AvatarGridSlice['primary']['profiles'][number];

  const renderProfileCard = (item: ProfileItem, uniqueKey: string) => (
    // Each profile card needs to be flex-shrink-0 to prevent shrinking
    // and have horizontal margins for spacing between cards.
    <div
      key={uniqueKey}
      className="flex-shrink-0 flex flex-col items-center text-center mx-4"
    >
      <div
        className={`flex flex-col items-center ${
          // This staggering effect (mb-8 or mt-8) is preserved from your original code.
          // It might look unique with horizontal scrolling.
          uniqueKey.includes('-0') ||
          uniqueKey.includes('-2') ||
          uniqueKey.includes('-4')
            ? 'mb-8'
            : 'mt-8'
          // A more robust way to handle staggering with duplicated content might be:
          // const originalIndex = parseInt(uniqueKey.split('-').pop() || '0');
          // originalIndex % 2 === 0 ? 'mb-8' : 'mt-8'
        }`}
      >
        {/* Render description/title above image for odd-indexed cards */}
        {(uniqueKey.includes('-1') ||
          uniqueKey.includes('-3') ||
          uniqueKey.includes('-5')) && (
          <div className="mb-4">
            <p className="font-semibold text-sm text-white">{item.title}</p>
            {'description' in item && item.description && (
              <p className="text-xs text-slate-300">{item.description}</p>
            )}
          </div>
        )}
        <div className="relative w-28 h-28 md:w-36 md:h-36">
          <PrismicNextImage
            field={item.avatar}
            className="rounded-2xl object-cover"
            fill
            sizes="(max-width: 768px) 112px, 144px"
          />
        </div>
        {/* Render description/title below image for even-indexed cards */}
        {(uniqueKey.includes('-0') ||
          uniqueKey.includes('-2') ||
          uniqueKey.includes('-4')) && (
          <div className="mt-4">
            <p className="font-semibold text-sm text-white">{item.title}</p>
            {'description' in item && item.description && (
              <p className="text-xs text-slate-300">{item.description}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );

  const profilesGrid = (
    // Outer container for the carousel. It hides any overflowing content.
    <div className="relative w-full overflow-hidden py-8">
      {/* The inner track that will hold the profile cards and be animated.
          'flex' arranges items horizontally, 'whitespace-nowrap' prevents wrapping. */}
      <div className="flex whitespace-nowrap animate-scroll-left">
        {/* Render profiles twice to create a seamless infinite loop.
            Unique keys are crucial for React's reconciliation. */}
        {slice.primary.profiles.map((item, index) =>
          renderProfileCard(item, `${item.title}-${index}-first`),
        )}
        {slice.primary.profiles.map((item, index) =>
          renderProfileCard(item, `${item.title}-${index}-second`),
        )}
      </div>

      {/* Tailwind CSS for the animation.
          This block defines the keyframes for the continuous scrolling. */}
      <style jsx>{`
        @keyframes scroll-left {
          0% {
            transform: translateX(0%);
          }
          100% {
            // This moves the track by exactly 50% of its total width.
            // Since the content is duplicated (e.g., A B C A B C),
            // moving by 50% means moving exactly one full set of profiles (A B C).
            // This creates the illusion of an infinite loop when it resets.
            transform: translateX(-50%);
          }
        }

        .animate-scroll-left {
          // Adjust the animation duration (e.g., 30s) to control scroll speed.
          // 'linear' ensures constant speed, 'infinite' makes it loop forever.
          animation: scroll-left var(--scroll-speed, 5s) linear infinite;
        }
      `}</style>
    </div>
  );

  if (variation === 'avatarGWithoutBgHeadline') {
    return (
      <section
        data-slice-type={slice.slice_type}
        data-slice-variation={slice.variation}
        className="relative container mx-auto px-4 pt-[2dvh] pb-[4dvh]"
      >
        {profilesGrid}
      </section>
    );
  }

  // Default variation: simple_centered
  return (
    <Bounded
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
    >
      {'title' in slice.primary && slice.primary.title && (
        <PrismicRichText field={slice.primary.title} components={components} />
      )}
      {profilesGrid}
    </Bounded>
  );
};

export default AvatarGrid;
